import { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import { useByxT } from '../shared/translations';
import { openSans, spaceGrotesk } from '../theme/fonts';
import { byxScheme } from '../theme/tokens';

/**
 * The card frame of the entity pages (UI spec "Card"): surface, a hairline
 * border, radius 14, padding 20, a 15px title with an optional caption and
 * actions on its right. The `table` variant has no padding and a title row
 * over a divider, so a table can sit flush with the panel edges.
 */

const useStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    card: {
      minWidth: 0,
      padding: theme.spacing(2.5),
      backgroundColor: scheme.panel,
      border: `1px solid ${scheme.line}`,
      borderRadius: 14,
    },
    table: {
      padding: 0,
      overflow: 'hidden',
    },
    head: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1, 1.25),
      minHeight: 22,
    },
    tableHead: {
      padding: theme.spacing(2, 2, 1.75, 2.5),
      borderBottom: `1px solid ${scheme.line}`,
    },
    title: {
      margin: 0,
      flex: '1 1 auto',
      minWidth: 0,
      fontFamily: spaceGrotesk,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: '22px',
      color: scheme.ink,
    },
    // A title followed by an aside (e.g. the Definição format tag).
    titleFit: {
      flex: '0 1 auto',
    },
    aside: {
      display: 'inline-flex',
      flex: '1 1 auto',
      minWidth: 0,
    },
    // body-small (UI v2): Open Sans 12px 400, muted-text, not the title
    // font, 6 px after the title (U3-4).
    count: {
      marginLeft: 6,
      fontFamily: openSans,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '17px',
      color: scheme.mutedText,
    },
    caption: {
      fontSize: 12,
      lineHeight: '17px',
      color: scheme.mutedText,
    },
    actions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    body: {
      marginTop: 14,
    },
    tableBody: {
      minWidth: 0,
      overflowX: 'auto',
    },
    message: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      margin: 0,
      fontSize: 13,
      lineHeight: '19px',
      color: scheme.ink,
    },
    tableMessage: {
      padding: theme.spacing(2, 2.5),
    },
    // body-medium muted-text, e.g. the empty Infraestrutura body (D-11).
    muted: {
      fontFamily: openSans,
      fontWeight: 400,
      color: scheme.mutedText,
    },
    errorIcon: {
      flex: 'none',
      fontSize: 16,
      color: scheme.error,
    },
    skeleton: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    tableSkeleton: {
      padding: theme.spacing(2, 2.5),
    },
    bar: {
      height: 12,
      borderRadius: 6,
      backgroundColor: scheme.surface2,
      animation: '$pulse 1.4s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    w90: { width: '90%' },
    w70: { width: '70%' },
    w50: { width: '50%' },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.55 },
    },
    visuallyHidden: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      border: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
  };
});

/** Default gap between the title-row actions (Relações controls: 10px). */
export const DEFAULT_ACTIONS_GAP = 10;

/** The gap of the title-row actions, a parameter of the card (UI v2). */
const useActionsStyles = makeStyles({
  gap: { gap: (props: { gap: number }) => props.gap },
});

export type ByxCardProps = {
  title: string;
  /** Shown right after the title, e.g. the number of rows. */
  count?: number;
  caption?: ReactNode;
  actions?: ReactNode;
  /**
   * Gap between the actions in px: 10 by default (Relações), 8 for the
   * Infraestrutura buttons (UI v2).
   */
  actionsGap?: number;
  variant?: 'card' | 'table';
  children?: ReactNode;
  className?: string;
  /** Extra class of the title row, e.g. a denser padding. */
  headClassName?: string;
  /** Shown right beside the title, outside the heading (e.g. a tag). */
  titleAside?: ReactNode;
};

export function ByxCard(props: Readonly<ByxCardProps>) {
  const {
    title,
    count,
    caption,
    actions,
    actionsGap = DEFAULT_ACTIONS_GAP,
    variant = 'card',
    children,
    className,
    headClassName,
    titleAside,
  } = props;
  const classes = useStyles();
  const actionClasses = useActionsStyles({ gap: actionsGap });
  const isTable = variant === 'table';
  return (
    <section
      className={[classes.card, isTable ? classes.table : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      aria-label={title}
    >
      <div
        className={[
          classes.head,
          isTable ? classes.tableHead : '',
          headClassName ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <h2
          className={`${classes.title} ${titleAside ? classes.titleFit : ''}`}
        >
          {title}
          {count !== undefined && (
            <span className={classes.count}>{count}</span>
          )}
        </h2>
        {titleAside && <span className={classes.aside}>{titleAside}</span>}
        {caption !== undefined && caption !== null && caption !== '' && (
          <span className={classes.caption}>{caption}</span>
        )}
        {actions && (
          <div className={`${classes.actions} ${actionClasses.gap}`}>
            {actions}
          </div>
        )}
      </div>
      {children !== undefined && children !== null && (
        <div className={isTable ? classes.tableBody : classes.body}>
          {children}
        </div>
      )}
    </section>
  );
}

/** Skeleton bars of a card body (UI spec "Loading"), by width class. */
const SKELETON_WIDTHS = ['w90', 'w70', 'w50'] as const;

/** Three pulsing bars at 90 / 70 / 50 % of the width, 12px high. */
export function CardSkeleton(
  props: Readonly<{ label: string; variant?: 'card' | 'table' }>,
) {
  const classes = useStyles();
  return (
    <output
      className={`${classes.skeleton} ${
        props.variant === 'table' ? classes.tableSkeleton : ''
      }`}
    >
      <span className={classes.visuallyHidden}>{props.label}</span>
      {SKELETON_WIDTHS.map(width => (
        <span
          key={width}
          aria-hidden="true"
          className={`${classes.bar} ${classes[width]}`}
        />
      ))}
    </output>
  );
}

/**
 * The body of a card whose source failed: a 16px error icon and the generic
 * message. No retry and never the raw error (SR-12).
 */
export function CardError(
  props: Readonly<{ title: string; variant?: 'card' | 'table' }>,
) {
  const classes = useStyles();
  const { t } = useByxT();
  return (
    <p
      className={`${classes.message} ${
        props.variant === 'table' ? classes.tableMessage : ''
      }`}
      role="alert"
    >
      <ErrorIcon aria-hidden className={classes.errorIcon} />
      {t('entity.card.error', { card: props.title })}
    </p>
  );
}

const useTableSkeletonStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    rows: { display: 'flex', flexDirection: 'column' },
    row: {
      display: 'flex',
      gap: theme.spacing(3),
      padding: theme.spacing(2.25, 1.5, 2.25, 2.5),
      '& + &': { borderTop: `1px solid ${scheme.line}` },
    },
    bar: {
      flex: '1 1 0',
      height: 12,
      borderRadius: 6,
      backgroundColor: scheme.surface2,
      animation: '$pulse 1.4s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      '&:first-child': { flexGrow: 3 },
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.55 },
    },
    hidden: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      border: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
  };
});

/** Rows of a table skeleton, as in the Catálogo. */
export const TABLE_SKELETON_ROWS = 4;

/**
 * Four skeleton rows of a table panel, each with its column bars. Without a
 * label the rows are decorative: a status around them announces the load.
 */
export function TableSkeleton(
  props: Readonly<{ label?: string; columns?: number }>,
) {
  const classes = useTableSkeletonStyles();
  const columns = props.columns ?? 3;
  if (props.label === undefined) {
    return (
      <div aria-hidden="true" className={classes.rows}>
        {Array.from({ length: TABLE_SKELETON_ROWS }, (_, row) => (
          <div key={row} className={classes.row}>
            {Array.from({ length: columns }, (__, column) => (
              <span key={column} className={classes.bar} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <output className={classes.rows}>
      <span className={classes.hidden}>{props.label}</span>
      {Array.from({ length: TABLE_SKELETON_ROWS }, (_, row) => (
        <div key={row} aria-hidden="true" className={classes.row}>
          {Array.from({ length: columns }, (__, column) => (
            <span key={column} className={classes.bar} />
          ))}
        </div>
      ))}
    </output>
  );
}

/** A plain one-line message inside a card body, in ink or muted-text. */
export function CardMessage(
  props: Readonly<{
    children: ReactNode;
    variant?: 'card' | 'table';
    tone?: 'ink' | 'muted';
  }>,
) {
  const classes = useStyles();
  return (
    <p
      className={[
        classes.message,
        props.variant === 'table' ? classes.tableMessage : '',
        props.tone === 'muted' ? classes.muted : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {props.children}
    </p>
  );
}

/** A card that is loading: its real title, then the skeleton. */
export function LoadingCard(
  props: Readonly<{
    title: string;
    caption?: ReactNode;
    variant?: 'card' | 'table';
  }>,
) {
  const { t } = useByxT();
  return (
    <ByxCard
      title={props.title}
      caption={props.caption}
      variant={props.variant}
    >
      <CardSkeleton
        label={t('entity.card.loading', { card: props.title })}
        variant={props.variant}
      />
    </ByxCard>
  );
}

/** A card whose source failed: its real title, then the error message. */
export function ErrorCard(
  props: Readonly<{
    title: string;
    caption?: ReactNode;
    variant?: 'card' | 'table';
  }>,
) {
  return (
    <ByxCard
      title={props.title}
      caption={props.caption}
      variant={props.variant}
    >
      <CardError title={props.title} variant={props.variant} />
    </ByxCard>
  );
}
